import { useEffect, useMemo, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { useLiveQuery } from '@/store/hooks';
import { listConversations } from '@/store/localStore';
import type { ConversationRecord } from '@/store/types';
import type { Hex } from '@/store/ids';
import dialogStyles from '@/components/DeleteDialog.module.css';
import styles from '@/components/panels.module.css';

export interface ForwardDialogProps {
  /** Refused when the source conversation has protected content. */
  sourceProtected: boolean;
  onForward: (destinationId: Hex) => void;
  onClose: () => void;
}

/**
 * Choosing where to forward a message.
 *
 * Section 06: "Protected-content conversations reject forwarding and client
 * download/save actions." The refusal is stated here rather than the control
 * being hidden, because the user pressed Forward and deserves to know why it
 * did not happen. The server refuses it too; this is the explanation, not the
 * enforcement.
 *
 * Forwarding "creates a new message through normal send semantics", so the
 * chosen destination goes through the outbox exactly like a send and the dialog
 * closes as soon as the operation is queued rather than waiting for a commit.
 */
export function ForwardDialog(props: ForwardDialogProps) {
  const { t } = useStrings();
  const [query, setQuery] = useState('');
  const conversations = useLiveQuery<ConversationRecord[]>(listConversations, [], []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [props]);

  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return conversations
      .filter((conversation) => !conversation.accessRevoked && !conversation.provisional)
      .filter(
        (conversation) =>
          needle.length === 0 || conversation.title.toLocaleLowerCase().includes(needle),
      );
  }, [conversations, query]);

  return (
    <div className={dialogStyles.backdrop}>
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="forward-title"
      >
        <h2 id="forward-title" className={dialogStyles.title}>
          {t('forward.title')}
        </h2>

        {props.sourceProtected ? (
          <>
            <p className={styles.error} role="alert">
              {t('forward.protected')}
            </p>
            <div className={dialogStyles.actions}>
              <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
                {t('forward.cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className={dialogStyles.description}>{t('forward.description')}</p>

            <div className={styles.field}>
              <label htmlFor="forward-search">{t('forward.search')}</label>
              <input
                id="forward-search"
                className={styles.input}
                type="search"
                dir="auto"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
            </div>

            {visible.length === 0 ? (
              <p className={styles.empty}>{t('inbox.emptySearch')}</p>
            ) : (
              <ul className={styles.list}>
                {visible.map((conversation) => (
                  <li key={conversation.id}>
                    <button
                      type="button"
                      className={styles.row}
                      style={{ width: '100%' }}
                      onClick={() => {
                        props.onForward(conversation.id);
                      }}
                    >
                      <span className={styles.rowBody}>
                        <span className={styles.rowTitle} dir="auto">
                          {conversation.title || t('conversation.unknownSender')}
                        </span>
                        <span className={styles.rowMeta}>{t('forward.send')}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className={dialogStyles.actions}>
              <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
                {t('forward.cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
